/*
 * FreezeFrame one-of-one cart guard.
 *
 * Each scene is a SINGLE physical 35mm cell. It is sold as 8 variants (4 woods x
 * Natural/LED) so the customer can choose a finish - but only one of those can
 * ever be fulfilled. Shopify tracks stock per VARIANT, so nothing native stops
 * someone putting the same cell in the basket eight times, once per wood.
 *
 * This keeps the cart honest AND explains itself. Picking a second wood for a
 * cell already in the basket is almost always "actually, I'd prefer teak", so we
 * treat it as a swap: keep the newest choice, drop the old one, and say clearly
 * what happened and why. Silently deleting a line would be baffling.
 *
 * How it triggers: by wrapping fetch/XHR and re-checking after any request to a
 * /cart/ write endpoint. That is deliberate - it does not depend on the theme's
 * event names, which is what broke an earlier attempt (Tinker fires cart events
 * on `document`, and the guard listened on `document.documentElement`; those
 * events bubble UP to document, so it never fired). Theme events are also
 * listened for, on document, as a second trigger.
 *
 * IMPORTANT: this is the friendly explanation, not the lock. It is client side
 * and bypassable. The real enforcement is the cart/checkout validation Function
 * (extensions/freeze-frame-one-of-one), which runs inside checkout before
 * payment. Do not treat this file as the guarantee.
 */
(function () {
  'use strict';

  var NOTICE_ID = 'ff-one-of-one-notice';
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Set while the guard is doing its own cart write, so wrapping fetch cannot
  // make it retrigger itself forever.
  var busy = false;

  function isCartWrite(url) {
    return typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url);
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /** "Ebony / Natural" from a cart line, falling back to the variant title. */
  function describe(line) {
    if (line.options_with_values && line.options_with_values.length) {
      return line.options_with_values
        .map(function (o) {
          return o.value;
        })
        .join(' / ');
    }
    return line.variant_title || '';
  }

  /*
   * Shopify's own refusal when you try to add a second of the same variant is
   * "The maximum quantity of this item is already in your cart." That is
   * server-side, is not in the theme's locale file, and cannot be edited. It is
   * also true but meaningless here: it reads like a purchase limit we imposed,
   * when the real reason is that the thing is one single piece of film.
   *
   * So when an add is refused, say why. The explanation is true whichever way
   * the add failed, whether the strip is already in the basket or somebody else
   * bought it, which matters because the response body does not reliably tell
   * the two apart.
   */
  function showRefusedNotice(detail) {
    try {
      var existing = document.getElementById(NOTICE_ID);
      if (existing) existing.remove();
      var el = document.createElement('div');
      el.id = NOTICE_ID;
      el.className = 'ff-oneofone-notice';
      el.setAttribute('role', 'alert');
      el.innerHTML =
        '<div class="ff-oneofone__inner">' +
        '<p class="ff-oneofone__title">There is only one of this film strip.</p>' +
        '<p>Every FreezeFrame is a single piece of 35mm film, cut once, so only ' +
        'one can ever be bought. ' +
        (detail ? esc(detail) : '') +
        '</p></div><button type="button" class="ff-oneofone__close" aria-label="Dismiss">&times;</button>';
      el.querySelector('.ff-oneofone__close').addEventListener('click', function () {
        el.remove();
      });
      document.body.appendChild(el);
      setTimeout(function () {
        if (el.parentNode) el.remove();
      }, 12000);
    } catch (e) {
      /* a notice failing must never break the cart */
    }
  }

  function showNotice(swaps) {
    try {
      var existing = document.getElementById(NOTICE_ID);
      if (existing) existing.remove();

      var body = swaps
        .map(function (s) {
          var line =
            '<p class="ff-oneofone__title">Only one “' + esc(s.title) + '” can be ordered.</p>';
          if (s.removed && s.kept && s.removed !== s.kept) {
            line +=
              '<p>It’s a single piece of film. The wood and finish are just how that one cell ' +
              'is framed. We’ve switched your choice from <b>' +
              esc(s.removed) +
              '</b> to <b>' +
              esc(s.kept) +
              '</b>.</p>';
          } else {
            line +=
              '<p>It’s a single piece of film, so there’s only ever one to sell. ' +
              'We’ve kept one in your basket.</p>';
          }
          return line;
        })
        .join('');

      var el = document.createElement('div');
      el.id = NOTICE_ID;
      el.className = 'ff-oneofone-notice';
      el.setAttribute('role', 'alert');
      el.innerHTML =
        '<div class="ff-oneofone__inner">' +
        body +
        '</div><button type="button" class="ff-oneofone__close" aria-label="Dismiss">&times;</button>';
      el.querySelector('.ff-oneofone__close').addEventListener('click', function () {
        el.remove();
      });
      document.body.appendChild(el);
      // Long enough to read, short enough not to linger.
      setTimeout(function () {
        if (el.parentNode) el.remove();
      }, 14000);
    } catch (e) {
      /* a notice failing must never break the cart */
    }
  }

  function tidy() {
    if (busy) return Promise.resolve();
    return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        if (!cart || !cart.items || !cart.items.length) return;

        // Group line items by product. Later entries are the more recent choice.
        var byProduct = {};
        cart.items.forEach(function (item) {
          (byProduct[item.product_id] = byProduct[item.product_id] || []).push(item);
        });

        var updates = {};
        var swaps = [];
        Object.keys(byProduct).forEach(function (pid) {
          var lines = byProduct[pid];
          var keep = lines[lines.length - 1];
          if (lines.length === 1 && keep.quantity <= 1) return;

          var dropped = lines.filter(function (l) {
            return l.key !== keep.key;
          });
          swaps.push({
            title: keep.product_title || keep.title,
            kept: describe(keep),
            removed: dropped.length ? describe(dropped[dropped.length - 1]) : ''
          });
          lines.forEach(function (line) {
            updates[line.key] = line.key === keep.key ? 1 : 0;
          });
        });

        if (!swaps.length) return;

        busy = true;
        return fetch(ROOT + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates: updates })
        })
          .then(function () {
            showNotice(swaps);
            // Ask the theme to redraw its cart UI. Tinker listens on document.
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            document.dispatchEvent(new CustomEvent('cart:update', { bubbles: true }));
          })
          .finally(function () {
            busy = false;
          });
      })
      .catch(function () {
        busy = false;
      });
  }

  function schedule() {
    setTimeout(tidy, 250);
  }

  // --- trigger 1: any cart write, whoever makes it -------------------------
  if (window.fetch) {
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : input && input.url;
      var p = origFetch.apply(this, arguments);
      if (!busy && isCartWrite(url)) {
        p.then(function (res) {
          // A refused ADD is the one-of-one rule being hit. Clone before
          // reading: the page still needs to consume the real response.
          if (res && res.ok === false && /\/cart\/add/.test(url)) {
            res.clone().json().then(function (body) {
              showRefusedNotice(body && body.description);
            }).catch(function () {
              showRefusedNotice('');
            });
          }
          schedule();
        }).catch(function () {});
      }
      return p;
    };
  }

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (!busy && isCartWrite(url)) this.addEventListener('load', schedule);
    return origOpen.apply(this, arguments);
  };

  // --- trigger 2: theme events (on document - that is where Tinker fires) ---
  ['cart:update', 'cart:add', 'variant:update'].forEach(function (evt) {
    document.addEventListener(evt, schedule);
  });

  // --- trigger 3: every page load ------------------------------------------
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tidy);
  } else {
    tidy();
  }
})();
